import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { checkoutStatus } from '../lib/api';
import { useApp } from '../context/AppContext';
import { CheckCircle2, Loader2, XCircle, ArrowRight } from 'lucide-react';

export default function BillingResult({ kind = 'plan' }) {
  const loc = useLocation();
  const nav = useNavigate();
  const { refreshMe } = useApp();
  const [state, setState] = useState({ status: 'pending', attempts: 0, data: null, error: null });

  useEffect(() => {
    const sid = new URLSearchParams(loc.search).get('session_id');
    if (!sid) { setState(s => ({ ...s, status: 'missing' })); return; }
    let cancelled = false;
    const poll = async (n) => {
      if (cancelled) return;
      if (n > 10) { setState(s => ({ ...s, status: 'timeout' })); return; }
      try {
        const d = await checkoutStatus(sid);
        if (cancelled) return;
        if (d.payment_status === 'paid') {
          setState({ status: 'paid', data: d, attempts: n });
          try { await refreshMe(); } catch {}
          return;
        }
        if (d.status === 'expired') {
          setState({ status: 'expired', data: d, attempts: n });
          return;
        }
        setState(s => ({ ...s, status: 'pending', attempts: n, data: d }));
        setTimeout(() => poll(n + 1), 2000);
      } catch (err) {
        setState(s => ({ ...s, status: 'error', error: err?.response?.data?.detail || err.message }));
      }
    };
    poll(0);
    return () => { cancelled = true; };
  }, [loc.search, refreshMe]);

  const title = kind === 'ad' ? 'Ad campaign' : 'Subscription';

  return (
    <div className="min-h-[70vh] flex items-center justify-center" data-testid={`billing-result-${kind}`}>
      <div className="card-shell p-10 max-w-[520px] text-center">
        {state.status === 'pending' && (
          <>
            <Loader2 className="w-12 h-12 mx-auto text-[#22d3ee] animate-spin" />
            <h1 className="font-display text-[28px] mt-4">Finalizing your payment…</h1>
            <p className="text-white/60 text-[14px] mt-2">Hang tight — we&apos;re confirming with Stripe (attempt {state.attempts + 1}/10).</p>
          </>
        )}
        {state.status === 'paid' && (
          <>
            <div className="w-16 h-16 mx-auto rounded-full grid place-items-center" style={{ background: 'rgba(34,211,238,0.15)', border: '1px solid rgba(34,211,238,0.4)' }}>
              <CheckCircle2 className="w-9 h-9 text-[#22d3ee]" />
            </div>
            <h1 className="font-display text-[32px] mt-5">Payment successful</h1>
            <p className="text-white/70 text-[14px] mt-2">Your {title} is active. ${(state.data?.amount_total / 100 || 0).toFixed(2)} {String(state.data?.currency || 'usd').toUpperCase()}</p>
            <button onClick={() => nav(kind === 'ad' ? '/ads' : '/home')} className="btn-primary mt-6 mx-auto" data-testid="billing-continue">
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          </>
        )}
        {['expired', 'timeout', 'error', 'missing'].includes(state.status) && (
          <>
            <div className="w-16 h-16 mx-auto rounded-full grid place-items-center" style={{ background: 'rgba(255,107,138,0.12)', border: '1px solid rgba(255,107,138,0.4)' }}>
              <XCircle className="w-9 h-9 text-[#ff6b8a]" />
            </div>
            <h1 className="font-display text-[28px] mt-5">
              {state.status === 'missing' && 'Missing session'}
              {state.status === 'expired' && 'Session expired'}
              {state.status === 'timeout' && 'Confirmation timed out'}
              {state.status === 'error' && 'Something went wrong'}
            </h1>
            <p className="text-white/65 text-[14px] mt-2">{state.error || 'Please try again from the billing page. If the charge succeeded we\'ll still activate it automatically.'}</p>
            <button onClick={() => nav(kind === 'ad' ? '/ads' : '/memberships')} className="btn-ghost mt-6 mx-auto">
              Back
            </button>
          </>
        )}
      </div>
    </div>
  );
}
